import type { GpuServerStartBody } from '../../../../types/models/GpuServer';
import type { GpuServerStartOutcome } from '../../../../hooks/useStartGpuServer';

// Pure logic of the GPU server start wizard: step ids, where a start refusal belongs, and the
// idempotency-key / step-lock rules that stop a customer from being charged twice
// (docs/api-types/gpu-servers.md "Start a GPU server"; frontend brief §1.6 and §1.8).

export const STEP_IDS = {
  server: 'gpu-start-server',
  location: 'gpu-start-location',
  image: 'gpu-start-image',
  keys: 'gpu-start-keys',
  review: 'gpu-start-review',
} as const;

export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];

/** The steps the user edits — locked (disabled) after an unconfirmed start. */
export type EditableStepId = Exclude<StepId, typeof STEP_IDS.review>;

/** 1–5 SSH keys per server. */
export const MIN_KEYS = 1;
export const MAX_KEYS = 5;

/** A start refusal's `data.<field>` → the step that owns it (checked in this order). */
const FIELD_STEPS: ReadonlyArray<[string, EditableStepId]> = [
  ['product_id', STEP_IDS.server],
  ['location_code', STEP_IDS.location],
  ['image', STEP_IDS.image],
  ['pubkey_ids', STEP_IDS.keys],
];

/**
 * The same messages by text, used only when a refusal names no field (every documented one does;
 * this keeps a jump working should the `data` map ever be missing).
 */
const MESSAGE_STEPS: ReadonlyArray<[string, EditableStepId]> = [
  ['Unknown product', STEP_IDS.server],
  ['This product cannot be started', STEP_IDS.server],
  ['This product has no price', STEP_IDS.server],
  ['This product cannot be started at the moment', STEP_IDS.server],
  ['location_code is invalid', STEP_IDS.location],
  ['This server is not available in this location at the moment', STEP_IDS.location],
  ['This image is not available for this server', STEP_IDS.image],
  ['Choose between 1 and 5 SSH keys', STEP_IDS.keys],
  ['One of the SSH keys is unknown or is not a valid OpenSSH public key', STEP_IDS.keys],
  ['One of the SSH keys could not be registered, please check it', STEP_IDS.keys],
];

/** The two balance refusals ("… X.XX EUR needed" and its race variant): offer a Top up. */
const BALANCE_MESSAGE_PREFIX = 'Your prepaid balance does not cover the first hour of this server';

export function isBalanceRefusal(message: string): boolean {
  return message.startsWith(BALANCE_MESSAGE_PREFIX);
}

export interface StartErrorPlacement {
  /** The step to jump to and show the message on; `null` = the review step's Alert. */
  step: EditableStepId | null;
  /** The message to show there, verbatim (the field's own message when it has one). */
  message: string;
  /** A balance refusal: add a Top up action. */
  showTopUp: boolean;
}

/**
 * Where a JSend `error` from the start belongs. While the steps are locked by an earlier unconfirmed
 * submit, nothing jumps: the message stays on review next to Retry (same key). `idempotency_key`
 * (a bug on our side) also stays on review.
 */
export function placeStartError(
  message: string,
  fieldErrors: Record<string, string>,
  locked: boolean,
): StartErrorPlacement {
  const showTopUp = isBalanceRefusal(message);
  if (locked) {
    return { step: null, message, showTopUp };
  }
  for (const [field, step] of FIELD_STEPS) {
    const fieldMessage = fieldErrors[field];
    if (typeof fieldMessage === 'string' && fieldMessage !== '') {
      return { step, message: fieldMessage, showTopUp: false };
    }
  }
  if (typeof fieldErrors.idempotency_key === 'string' && fieldErrors.idempotency_key !== '') {
    return { step: null, message: fieldErrors.idempotency_key, showTopUp: false };
  }
  const byMessage = MESSAGE_STEPS.find(([text]) => text === message);
  if (byMessage) {
    return { step: byMessage[1], message, showTopUp: false };
  }
  return { step: null, message, showTopUp };
}

/**
 * One wizard session's submit state. `key` is reused for EVERY submit of the session; `locked` is
 * set by an unconfirmed submit and kept until a success, and `lockedBody` is then the exact body
 * that went out (a Retry resends it — edited inputs under that key would replay the OLD server).
 */
export interface SubmitSession {
  key: string;
  locked: boolean;
  lockedBody: GpuServerStartBody | null;
}

export function initialSession(key: string): SubmitSession {
  return { key, locked: false, lockedBody: null };
}

/**
 * The session after a submit of `body` resolved to `outcome`:
 * - `unconfirmed` → locked on that body (the first request may still create a paid server);
 * - JSend `error` → unchanged: unlocked, nothing was debited, the user may edit and resubmit with
 *   the same key; still locked if it was (that request may still commit under the key);
 * - `success` with a `failed` server → unlocked with a NEW key (resending the old one would only
 *   replay the failed server);
 * - any other `success` → unlocked (the page navigates to the returned server).
 */
export function nextSession(
  session: SubmitSession,
  body: GpuServerStartBody,
  outcome: GpuServerStartOutcome,
  newKey: () => string,
): SubmitSession {
  switch (outcome.status) {
    case 'unconfirmed':
      return { key: session.key, locked: true, lockedBody: session.lockedBody ?? body };
    case 'error':
      return session;
    case 'success':
      return outcome.server.status === 'failed'
        ? initialSession(newKey())
        : { key: session.key, locked: false, lockedBody: null };
    default:
      return session;
  }
}

/** What Start (or Retry) would be blocked by, first reason first; `[]` = enabled. */
export interface StartBlockerInput {
  isImpersonating: boolean;
  startsEnabled: boolean;
  startsReason: string | null;
  activeCount: number;
  maxServers: number;
  stepsValid: boolean;
  acknowledged: boolean;
}

export function startBlockers(input: StartBlockerInput): string[] {
  const blockers: string[] = [];
  if (input.isImpersonating) {
    blockers.push('Starting a GPU server is not available while impersonating.');
  }
  if (!input.startsEnabled) {
    blockers.push(input.startsReason ?? 'GPU servers are not available at the moment');
  }
  if (input.activeCount >= input.maxServers) {
    blockers.push(`You already run the maximum number of GPU servers (${input.maxServers}).`);
  }
  if (!input.stepsValid) {
    blockers.push('Complete every step first.');
  }
  if (!input.acknowledged) {
    blockers.push('Tick the acknowledgement to start the server.');
  }
  return blockers;
}
